/**
 * /backgammon — the rules of LONG nardy (длинные нарды) and SHORT nardy
 * (короткие нарды = backgammon, tavla, takhteh). Pure: no DOM, no randomness of
 * its own — the page, the bot worker and the node tests all run this file.
 *
 * ── Sources ──────────────────────────────────────────────────────────────────
 *  [MS]  «Правила вида спорта "нарды"», утв. приказом Минспорта России от
 *        22.07.2024 № 734 (legalacts.ru/doc/pravila-vida-sporta-nardy-utv-prikazom-minsporta-rossii-ot/):
 *        art. 20 (long nardy), 21 (short), 26.3 (who starts), 27 (bearing off),
 *        1.21–1.22 (mars / koks), 23.14 (Crawford).
 *  [AK]  N. Akhundov, «Справочник по длинным нардам. Теория и практика игры», 2012.
 *  [WB]  World Backgammon Federation / bkgm.com «Rules of Backgammon» — the
 *        standard short-game rules (bar, entering, hitting, gammon, cube).
 *
 * ── LONG nardy, exactly as implemented ──────────────────────────────────────
 *  · 15 checkers each, all on the «head»; both sides move the same way round,
 *    the opponent's head is on the opposite side (12 points away). [MS 20.1–2]
 *  · A point holding even ONE opponent checker is closed; nothing is hit. [MS 20.4]
 *  · One checker may leave the head per turn. The only exception is a player's
 *    FIRST roll when it is 3-3, 4-4 or 6-6: two may leave, «despite obstacles
 *    set up by the first player». [MS 20.3] Why exactly these three: from the
 *    head a single checker can play 1-1, 2-2 and 5-5 in full, but 3-3, 4-4 and
 *    6-6 each run into the opponent's head after 3, 2 and 1 steps. Under the
 *    official start (the opening roll is the two single dice thrown to decide who
 *    starts, never a double — [MS 26.3]) only the SECOND player can meet it,
 *    which is how [MS] words it; home games where the winner rolls again give the
 *    first player the same exception [AK]. The engine grants it on each player's
 *    first turn, which is the same thing under either start.
 *  · A six-point block (six consecutive points, counted along the OPPONENT'S
 *    path) is legal only while at least one opponent checker is already past it;
 *    it may not be built even for a moment inside one turn. [MS 20.5] The check
 *    therefore runs after every single step, not on the finished turn.
 *  · Both dice must be played when possible; if only one can be and either
 *    could, the larger. Doubles play four times. [MS 20.6]
 *  · Bearing off starts once all 15 are home: exact number, or a higher number
 *    from the highest occupied point; a move inside home is allowed instead.
 *    [MS 27.1–27.4]
 *  · Result: «оин» 1 point (loser bore off at least one), «марс» 2 (loser bore
 *    off none). [MS 1.21, 1.23] No draws. [MS 27.6] «Кокс» / home mars is NOT in
 *    the long game under [MS] («кокс применяется только в коротких нардах»); some
 *    home traditions pay 3 when the loser still has a checker in his starting
 *    quarter — offered as an option (koksLong), off by default.
 *
 * ── SHORT nardy / backgammon ────────────────────────────────────────────────
 *  Standard [WB] = [MS 21]: players move in opposite directions; a single
 *  opponent checker (blot) is hit and goes to the bar; two or more close a point;
 *  a checker on the bar must enter the opponent's home before anything else
 *  moves; the same both-dice / larger-die / bearing-off rules. Result: 1, gammon
 *  («марс») 2 — loser bore off none, backgammon («кокс») 3 — and a checker still
 *  on the bar or in the winner's home. [MS 1.21–1.22] Doubling cube and the
 *  Crawford rule [MS 23.14] live in game.js.
 *
 * ── Coordinates ─────────────────────────────────────────────────────────────
 *  Absolute board index a = 0..23 is White's own point number minus one, in both
 *  variants. Each player counts his own path q = 24 (start) … 1 (last point
 *  before bearing off); q = 25 is the bar, q = 0 is «off». pos.b[a] > 0 is White
 *  checkers, < 0 is Black.
 */

export const LONG = 'long';
export const SHORT = 'short';
export const WHITE = 0;
export const BLACK = 1;
export const BAR = 25;
export const OFF = 0;

/* REL2ABS[v][p][q] → absolute index, ABS2REL[v][p][a] → q */
export const REL2ABS = { long: [[], []], short: [[], []] };
export const ABS2REL = { long: [[], []], short: [[], []] };
for (const v of [LONG, SHORT]) {
    for (let q = 1; q <= 24; q++) {
        REL2ABS[v][0][q] = q - 1;
        const wq = v === SHORT ? 25 - q : (q > 12 ? q - 12 : q + 12);
        REL2ABS[v][1][q] = wq - 1;
    }
    for (let p = 0; p < 2; p++) for (let q = 1; q <= 24; q++) ABS2REL[v][p][REL2ABS[v][p][q]] = q;
}

/* Long game: the opponent's path, seen in the MOVER's coordinates. Symmetric —
   the same for both colours. S[i] (i = 0..23, 0 = opponent's head) → mover q. */
const LONG_S = [];
const LONG_SIDX = new Int8Array(25);
for (let i = 0; i < 24; i++) {
    const oq = 24 - i;                          // opponent's own point number
    const q = oq > 12 ? oq - 12 : oq + 12;      // same square in mover coords
    LONG_S[i] = q;
    LONG_SIDX[q] = i;
}

export const opp = (p) => 1 - p;

/** The starting position. */
export function initialPosition(v) {
    const b = new Array(24).fill(0);
    if (v === LONG) {
        b[REL2ABS.long[0][24]] = 15;
        b[REL2ABS.long[1][24]] = -15;
    } else {
        // 2 on 24, 5 on 13, 3 on 8, 5 on 6 — for each side in its own count
        for (const [q, n] of [[24, 2], [13, 5], [8, 3], [6, 5]]) {
            b[REL2ABS.short[0][q]] += n;
            b[REL2ABS.short[1][q]] -= n;
        }
    }
    return { v, b, bar: [0, 0], off: [0, 0] };
}

export function clonePos(pos) {
    return { v: pos.v, b: Array.from(pos.b), bar: pos.bar.slice(), off: pos.off.slice() };
}

/** Checkers of p at his own point q (q = 25 → bar, 0 → off). */
export function countAt(pos, p, q) {
    if (q === BAR) return pos.bar[p];
    if (q === OFF) return pos.off[p];
    const n = pos.b[REL2ABS[pos.v][p][q]];
    return p === WHITE ? (n > 0 ? n : 0) : (n < 0 ? -n : 0);
}

export function pipCount(pos, p) {
    let s = pos.bar[p] * 25;
    for (let q = 1; q <= 24; q++) s += q * countAt(pos, p, q);
    return s;
}

export function checkerTotal(pos, p) {
    let s = pos.bar[p] + pos.off[p];
    for (let q = 1; q <= 24; q++) s += countAt(pos, p, q);
    return s;
}

export function positionKey(pos) {
    let k = pos.v[0];
    for (let a = 0; a < 24; a++) k += String.fromCharCode(64 + pos.b[a]);
    return k + pos.bar[0] + '.' + pos.bar[1] + '.' + pos.off[0] + '.' + pos.off[1];
}

/** Dice to play: [a,b] or four of a double. */
export function diceToPlay(d1, d2) {
    return d1 === d2 ? [d1, d1, d1, d1] : [d1, d2];
}

/* ─────────────────────── the working board of one turn ──────────────────── */

/**
 * A mutable, mover-relative view used while a turn is generated or played.
 *   me[q]  own checkers at q (25 = bar, 0 = off)
 *   op[q]  opponent checkers on the square the mover calls q (q = 1..24)
 */
export function makeWork(pos, p, opts) {
    opts = opts || {};
    const v = pos.v, map = REL2ABS[v][p], sgn = p === WHITE ? 1 : -1;
    const me = new Int8Array(26), op = new Int8Array(25);
    let outside = 0;
    for (let q = 1; q <= 24; q++) {
        const n = pos.b[map[q]] * sgn;
        if (n > 0) { me[q] = n; if (q > 6) outside += n; }
        else if (n < 0) op[q] = -n;
    }
    me[BAR] = pos.bar[p];
    me[OFF] = pos.off[p];
    outside += pos.bar[p];
    return {
        v, p, me, op,
        opBar: pos.bar[1 - p], opOff: pos.off[1 - p],
        outside,
        heads: 0,
        headLimit: v === LONG ? (opts.headLimit || 1) : 99,
    };
}

/** Head allowance for this roll (long game). */
export function headLimitFor(v, d1, d2, firstTurn) {
    if (v !== LONG) return 99;
    if (firstTurn && d1 === d2 && (d1 === 3 || d1 === 4 || d1 === 6)) return 2;
    return 1;
}

function blocked(w, q) {
    return w.v === LONG ? w.op[q] > 0 : w.op[q] > 1;
}

/**
 * Long game, after the mover landed on t: does a run of ≥6 own points through t,
 * along the opponent's path, trap every opponent checker? [MS 20.5]
 */
export function longBlockViolation(w, t) {
    const S = LONG_S, me = w.me;
    const i0 = LONG_SIDX[t];
    let lo = i0, hi = i0;
    while (lo > 0 && me[S[lo - 1]] > 0) lo--;
    while (hi < 23 && me[S[hi + 1]] > 0) hi++;
    if (hi - lo + 1 < 6) return false;
    if (w.opOff > 0) return false;
    for (let i = hi + 1; i < 24; i++) if (w.op[S[i]] > 0) return false;
    return true;
}

/**
 * Can a checker of the mover go from `from` with die d right now? Returns
 * {ok, to, hit, reason}. Reasons: bar_first, no_checker, head, occupied,
 * not_home, higher_first, block6.
 */
export function stepCheck(w, from, d) {
    const me = w.me;
    if (me[BAR] > 0 && from !== BAR) return { ok: false, reason: 'bar_first' };
    if (from < 1 || from > 25 || me[from] <= 0) return { ok: false, reason: 'no_checker' };
    if (w.v === LONG && from === 24 && w.heads >= w.headLimit) return { ok: false, reason: 'head' };
    let to = from - d;
    if (to <= 0) {
        if (w.outside > 0) return { ok: false, reason: 'not_home' };
        if (to < 0) {
            for (let q = from + 1; q <= 6; q++) if (me[q] > 0) return { ok: false, reason: 'higher_first' };
        }
        return { ok: true, to: OFF, hit: false };
    }
    if (blocked(w, to)) return { ok: false, reason: 'occupied' };
    const hit = w.v === SHORT && w.op[to] === 1;
    if (w.v === LONG) {
        me[from]--; me[to]++;
        const bad = longBlockViolation(w, to);
        me[from]++; me[to]--;
        if (bad) return { ok: false, reason: 'block6' };
    }
    return { ok: true, to, hit };
}

/** Apply a checked step; returns an undo record. */
export function stepApply(w, from, to, hit, d) {
    const me = w.me;
    me[from]--;
    if (to === OFF) me[OFF]++; else me[to]++;
    if (hit) { w.op[to] = 0; w.opBar++; }
    const wasOut = from > 6;               // bar (25) counts as outside too
    const nowOut = to > 6;
    if (wasOut && !nowOut) w.outside--;
    if (w.v === LONG && from === 24) w.heads++;
    return { from, to, hit, d };
}

export function stepUndo(w, m) {
    const me = w.me;
    if (m.to === OFF) me[OFF]--; else me[m.to]--;
    me[m.from]++;
    if (m.hit) { w.op[m.to] = 1; w.opBar--; }
    if (m.from > 6 && !(m.to > 6)) w.outside++;
    if (w.v === LONG && m.from === 24) w.heads--;
}

/*
 * The state of a working board packed into five 28-bit integers — EXACT, not a
 * hash: own checkers me[0..25] (≤15 each, 4 bits), the opponent's bar, `extra`
 * (which dice are left), and the opponent's blots (short game: a hit changes
 * them; in the long game they never change). The search deduplicates on it.
 * A 51-character string key per node was 80 % of the time of generatePlays:
 * this is 16× faster on the short game, 3.3× on the long (27.09.2026), the
 * plays identical — checked against the string-key generator on 170 000
 * positions × rolls (tests/js/backgammon-generate-plays.test.mjs keeps it as
 * the oracle).
 */
const PK = new Int32Array(6);   // k0..k4, hash
function packWork(w, extra) {
    const me = w.me;
    PK[0] = me[0] | me[1] << 4 | me[2] << 8 | me[3] << 12 | me[4] << 16 | me[5] << 20 | me[6] << 24;
    PK[1] = me[7] | me[8] << 4 | me[9] << 8 | me[10] << 12 | me[11] << 16 | me[12] << 20 | me[13] << 24;
    PK[2] = me[14] | me[15] << 4 | me[16] << 8 | me[17] << 12 | me[18] << 16 | me[19] << 20 | me[20] << 24;
    PK[3] = me[21] | me[22] << 4 | me[23] << 8 | me[24] << 12 | me[25] << 16 | w.opBar << 20 | extra << 24;
    let k4 = 0;
    if (w.v === SHORT) { const op = w.op; for (let q = 1; q < 25; q++) if (op[q] === 1) k4 |= 1 << (q - 1); }
    PK[4] = k4;
    let h = Math.imul(PK[0] ^ 0x9E3779B9, 0x85EBCA6B);
    h = Math.imul(h ^ (h >>> 13) ^ PK[1], 0xC2B2AE35);
    h = Math.imul(h ^ (h >>> 16) ^ PK[2], 0x85EBCA6B);
    h = Math.imul(h ^ (h >>> 13) ^ PK[3], 0xC2B2AE35);
    h = Math.imul(h ^ (h >>> 16) ^ k4, 0x85EBCA6B);
    PK[5] = h ^ (h >>> 15);
}

/** Which dice are left, as a small number (order matters for two different dice). */
function restKey(r) {
    if (r.length === 2 && r[0] !== r[1]) return r[0] > r[1] ? 4 : 5;
    if (r.length === 1) return 8 + r[0];
    return r.length;
}

/**
 * The most dice that can still be played from here with `dice` (values),
 * searching until `need` is reached (early exit).
 */
export function maxPlayable(w, dice, need) {
    if (!dice.length) return 0;
    need = need == null ? dice.length : need;
    let best = 0;
    const tried = new Set();
    for (let i = 0; i < dice.length; i++) {
        const d = dice[i];
        if (tried.has(d)) continue;
        tried.add(d);
        const rest = dice.slice(0, i).concat(dice.slice(i + 1));
        for (let from = 25; from >= 1; from--) {
            if (w.me[from] <= 0) continue;
            const c = stepCheck(w, from, d);
            if (!c.ok) continue;
            const u = stepApply(w, from, c.to, c.hit, d);
            const r = 1 + maxPlayable(w, rest, need - 1);
            stepUndo(w, u);
            if (r > best) best = r;
            if (best >= need) return best;
        }
    }
    return best;
}

/**
 * All distinct results of a full turn.
 *   pos, p, d1, d2, opts.firstTurn (long head exception)
 * → { plays: [{ moves:[{from,to,hit,d}], pos }], maxUsed }
 * Each distinct final position appears once, with one sequence reaching it.
 * The both-dice / larger-die rule is applied.
 */
export function generatePlays(pos, p, d1, d2, opts) {
    opts = opts || {};
    const w = makeWork(pos, p, { headLimit: headLimitFor(pos.v, d1, d2, !!opts.firstTurn) });
    const dice = diceToPlay(d1, d2);
    const seen = new Map();       // hash → [k0..k4, k0..k4, …]  (exact; equal hashes chain)
    const leafAt = new Map();     // hash → [index into leaves, …]
    const leaves = [];            // {k: [k0..k4], moves, used} in the order first reached
    let maxUsed = 0;
    const seq = [];

    function seenBefore() {
        const h = PK[5], l = seen.get(h);
        if (l === undefined) { seen.set(h, [PK[0], PK[1], PK[2], PK[3], PK[4]]); return false; }
        for (let i = 0; i < l.length; i += 5) {
            if (l[i] === PK[0] && l[i + 1] === PK[1] && l[i + 2] === PK[2] && l[i + 3] === PK[3] && l[i + 4] === PK[4]) return true;
        }
        l.push(PK[0], PK[1], PK[2], PK[3], PK[4]);
        return false;
    }

    function rec(rest, rk) {
        packWork(w, rk);
        if (seenBefore()) return;
        let moved = false, tried = 0;
        for (let i = 0; i < rest.length; i++) {
            const d = rest[i];
            if (tried & (1 << d)) continue;
            tried |= 1 << d;
            const nrest = rest.slice(0, i).concat(rest.slice(i + 1));
            const nrk = restKey(nrest);
            for (let from = 25; from >= 1; from--) {
                if (w.me[from] <= 0) continue;
                const c = stepCheck(w, from, d);
                if (!c.ok) continue;
                moved = true;
                const u = stepApply(w, from, c.to, c.hit, d);
                seq.push(u);
                rec(nrest, nrk);
                seq.pop();
                stepUndo(w, u);
            }
        }
        if (!moved) {
            const used = seq.length;
            if (used > maxUsed) maxUsed = used;
            packWork(w, 0);
            let ids = leafAt.get(PK[5]), prev = null;
            if (ids === undefined) { ids = []; leafAt.set(PK[5], ids); }
            for (const id of ids) {
                const k = leaves[id].k;
                if (k[0] === PK[0] && k[1] === PK[1] && k[2] === PK[2] && k[3] === PK[3] && k[4] === PK[4]) { prev = leaves[id]; break; }
            }
            // one final position may be reached with either die (the last
            // checker borne off by a 3 or by a 5): keep the larger-die way, so
            // the larger-die filter below cannot drop a legal position
            if (!prev || prev.used < used || (used === 1 && prev.used === 1 && seq[0].d > prev.moves[0].d)) {
                const moves = seq.map((m) => ({ from: m.from, to: m.to, hit: m.hit, d: m.d }));
                if (prev) { prev.moves = moves; prev.used = used; }
                else { ids.push(leaves.length); leaves.push({ k: [PK[0], PK[1], PK[2], PK[3], PK[4]], moves, used }); }
            }
        }
    }
    // the root is marked apart from every later node (a root with dice left is never revisited anyway)
    rec(dice, restKey(dice) + 16);

    let list = [];
    for (const lf of leaves) if (lf.used === maxUsed) list.push({ moves: lf.moves, used: lf.used });
    if (d1 !== d2 && maxUsed === 1) {
        const hi = Math.max(d1, d2);
        const withHi = list.filter((lf) => lf.moves[0].d === hi);
        if (withHi.length) list = withHi;
    }
    const plays = list.map((lf) => ({ moves: lf.moves, pos: applyMoves(pos, p, lf.moves) }));
    // a pass (no move) is one «play» with no moves
    if (!plays.length) plays.push({ moves: [], pos: clonePos(pos) });
    return { plays, maxUsed };
}

/** Apply mover-relative steps to a position (no legality check). */
export function applyMoves(pos, p, moves) {
    const np = clonePos(pos);
    for (const m of moves) applyStepToPos(np, p, m);
    return np;
}

export function applyStepToPos(pos, p, m) {
    const map = REL2ABS[pos.v][p], sgn = p === WHITE ? 1 : -1;
    if (m.from === BAR) pos.bar[p]--;
    else pos.b[map[m.from]] -= sgn;
    if (m.to === OFF) pos.off[p]++;
    else {
        const a = map[m.to];
        if (m.hit) { pos.b[a] = 0; pos.bar[1 - p]++; }
        pos.b[a] += sgn;
    }
}

/* ─────────────────── a turn played step by step (the UI) ─────────────────── */

/**
 * The live turn: what the person may do next, and why not.
 *   const t = createTurn(pos, p, d1, d2, {firstTurn});
 *   t.legalSteps()          → [{from,to,hit,d}]   every step allowed NOW
 *   t.destinations(from)    → [{to, steps:[…]}]   incl. combined moves
 *   t.play(step) / t.undo()
 *   t.done                  → no more dice can be played (turn may be confirmed)
 *   t.whyNot(from, to)      → reason code for an attempted illegal move
 */
export function createTurn(pos, p, d1, d2, opts) {
    opts = opts || {};
    const start = clonePos(pos);
    const w = makeWork(pos, p, { headLimit: headLimitFor(pos.v, d1, d2, !!opts.firstTurn) });
    const all = diceToPlay(d1, d2);
    const maxUsed = maxPlayable(w, all.slice());
    const larger = d1 !== d2 ? Math.max(d1, d2) : 0;
    // «if either die could be played but not both — the larger»: only then does
    // the choice of die matter; find whether the larger die has any legal step
    let forceLarger = false;
    if (larger && maxUsed === 1) {
        for (let from = 25; from >= 1 && !forceLarger; from--) {
            if (w.me[from] > 0 && stepCheck(w, from, larger).ok) forceLarger = true;
        }
    }
    const played = [];
    let rest = all.slice();

    function allowed(from, d) {
        const c = stepCheck(w, from, d);
        if (!c.ok) return { ok: false, reason: c.reason };
        if (forceLarger && played.length === 0 && d !== larger) return { ok: false, reason: 'larger_die' };
        const i = rest.indexOf(d);
        const nrest = rest.slice(0, i).concat(rest.slice(i + 1));
        const u = stepApply(w, from, c.to, c.hit, d);
        const need = maxUsed - played.length - 1;
        const can = need <= 0 ? 0 : maxPlayable(w, nrest, need);
        stepUndo(w, u);
        if (can < need) return { ok: false, reason: 'max_dice' };
        return { ok: true, to: c.to, hit: c.hit, d };
    }

    const api = {
        p, d1, d2, maxUsed,
        get played() { return played.slice(); },
        get rest() { return rest.slice(); },
        get done() { return played.length >= maxUsed; },
        get work() { return w; },
        start,
        position() { return applyMoves(start, p, played); },
        legalSteps() {
            if (played.length >= maxUsed) return [];
            const out = [];
            const dv = [...new Set(rest)];
            for (let from = 25; from >= 1; from--) {
                if (w.me[from] <= 0) continue;
                for (const d of dv) {
                    const a = allowed(from, d);
                    if (a.ok) out.push({ from, to: a.to, hit: a.hit, d });
                }
            }
            return out;
        },
        /** Where the checker on `from` may go: single steps, and chains of
         *  steps by the same checker (e.g. 6+5 → 11 points), each chain made of
         *  individually allowed steps. */
        destinations(from) {
            const res = new Map();
            const walk = (q, chain) => {
                if (played.length + chain.length >= maxUsed) return;
                const dv = [...new Set(rest)];
                for (const d of dv) {
                    const a = allowed(q, d);
                    if (!a.ok) continue;
                    const step = { from: q, to: a.to, hit: a.hit, d };
                    const nchain = chain.concat([step]);
                    const prev = res.get(a.to);
                    if (!prev || prev.steps.length > nchain.length) res.set(a.to, { to: a.to, steps: nchain });
                    if (a.to !== OFF) {
                        api.play(step);
                        walk(a.to, nchain);
                        api.undo();
                    }
                }
            };
            walk(from, []);
            return [...res.values()];
        },
        play(step) {
            const a = allowed(step.from, step.d);
            if (!a.ok) return false;
            const u = stepApply(w, step.from, a.to, a.hit, step.d);
            played.push(u);
            const i = rest.indexOf(step.d);
            rest.splice(i, 1);
            return true;
        },
        undo() {
            const u = played.pop();
            if (!u) return false;
            stepUndo(w, u);
            rest.push(u.d);
            return true;
        },
        reset() { while (played.length) api.undo(); },
        /** Why can the checker on `from` not go to `to`? */
        whyNot(from, to) {
            if (played.length >= maxUsed) return 'turn_done';
            if (from !== BAR && w.me[BAR] > 0) return 'bar_first';
            const dv = [...new Set(rest)];
            let reason = null;
            for (const d of dv) {
                const target = from - d <= 0 ? OFF : from - d;
                if (to != null && target !== to && !(to === OFF && target === OFF)) continue;
                const a = allowed(from, d);
                if (a.ok) return null;
                reason = reason || a.reason;
                if (a.reason !== 'occupied' && a.reason !== 'no_checker') return a.reason;
            }
            if (reason) return reason;
            if (to != null && to !== OFF && w.v === LONG && w.op[to] > 0) return 'occupied';
            if (to != null && to !== OFF && w.v === SHORT && w.op[to] > 1) return 'occupied';
            return 'no_die';
        },
    };
    return api;
}

/* ─────────────────────────── end of game, scoring ───────────────────────── */

export function winner(pos) {
    if (pos.off[WHITE] === 15) return WHITE;
    if (pos.off[BLACK] === 15) return BLACK;
    return -1;
}

/**
 * Points for the winner (before the cube): 1 / 2 / 3 and the kind.
 * opts.koksLong — pay 3 in the long game when the loser has borne off none
 * and still has a checker in his starting quarter (off by default, see header).
 */
export function resultOf(pos, w, opts) {
    opts = opts || {};
    const l = 1 - w;
    if (pos.off[l] > 0) return { points: 1, kind: 'single' };
    let back = pos.bar[l] > 0;
    for (let q = 19; q <= 24 && !back; q++) if (countAt(pos, l, q) > 0) back = true;
    if (pos.v === SHORT) return back ? { points: 3, kind: 'koks' } : { points: 2, kind: 'mars' };
    if (opts.koksLong && back) return { points: 3, kind: 'koks' };
    return { points: 2, kind: 'mars' };
}

/** Is there still contact (can the sides still meet)? Race = no contact. */
export function isRace(pos) {
    // the furthest-back checker of each side, as a distance along White's path
    if (pos.v === SHORT) {
        if (pos.bar[0] || pos.bar[1]) return false;
        let wBack = 0, bBack = 25;
        for (let a = 0; a < 24; a++) {
            if (pos.b[a] > 0 && a + 1 > wBack) wBack = a + 1;          // White's highest point
            if (pos.b[a] < 0 && a + 1 < bBack) bBack = a + 1;          // Black's lowest (in White count)
        }
        return wBack < bBack;
    }
    // Long game: both sides run the same way. In White's numbering White goes
    // 24→1 and Black goes 12→1 then 24→13. Contact = some checker still has to
    // pass an enemy checker on its way home.
    let wMax = 0;
    for (let q = 1; q <= 24; q++) if (countAt(pos, WHITE, q) > 0) wMax = q;
    for (let q = 1; q <= 24; q++) {
        if (countAt(pos, BLACK, q) > 0 && REL2ABS.long[1][q] + 1 < wMax) return false;
    }
    let bMax = 0;
    for (let q = 1; q <= 24; q++) if (countAt(pos, BLACK, q) > 0) bMax = q;
    for (let q = 1; q <= 24; q++) {
        if (countAt(pos, WHITE, q) > 0 && ABS2REL.long[1][REL2ABS.long[0][q]] < bMax) return false;
    }
    return true;
}

/* ───────────────────────────── notation ─────────────────────────────────── */

/** «24/18 13/11*», «bar/20», «6/off», «8/4(2)». Mover-relative numbers. */
export function notate(moves) {
    if (!moves || !moves.length) return '';
    const pt = (q) => (q === BAR ? 'bar' : q === OFF ? 'off' : String(q));
    // merge chains of the same checker: a step starting where the previous ended
    const chains = [];
    const used = new Array(moves.length).fill(false);
    for (let i = 0; i < moves.length; i++) {
        if (used[i]) continue;
        used[i] = true;
        const ch = [moves[i]];
        let end = moves[i].to;
        for (let j = i + 1; j < moves.length && end !== OFF; j++) {
            if (!used[j] && moves[j].from === end) {
                used[j] = true; ch.push(moves[j]); end = moves[j].to;
            }
        }
        chains.push(ch);
    }
    const parts = chains.map((ch) => {
        // intermediate points are written only where something was hit:
        // 24/18/13 → 24/13, but 24/18*/13 stays
        let s = pt(ch[0].from);
        ch.forEach((m, k) => {
            if (m.hit || k === ch.length - 1) s += '/' + pt(m.to) + (m.hit ? '*' : '');
        });
        return s;
    });
    const counts = new Map();
    for (const s of parts) counts.set(s, (counts.get(s) || 0) + 1);
    const out = [];
    for (const [s, n] of counts) out.push(n > 1 ? s + '(' + n + ')' : s);
    return out.join(' ');
}

/* ───────────────────────────── dice ─────────────────────────────────────── */

/** A fair die from any byte source (rejection sampling: 252 = 6 × 42). */
export function dieFromBytes(bytes, startAt) {
    for (let i = startAt || 0; i < bytes.length; i++) {
        if (bytes[i] < 252) return { die: (bytes[i] % 6) + 1, next: i + 1 };
    }
    return null;
}

/** Two dice from a byte string (e.g. a SHA-256 digest). */
export function diceFromBytes(bytes) {
    const a = dieFromBytes(bytes, 0);
    if (!a) return null;
    const b = dieFromBytes(bytes, a.next);
    if (!b) return null;
    return [a.die, b.die];
}

/** Crypto-grade die for the page. */
export function cryptoDie() {
    const buf = new Uint8Array(8);
    for (;;) {
        crypto.getRandomValues(buf);
        const r = dieFromBytes(buf, 0);
        if (r) return r.die;
    }
}
